/**
 * SNS Links Panel Handler
 *
 * Event handler layer: handle{Action}(event)
 */

import { SNSLinksUtils } from "./sns-links-utils";
import { SNSLinksPanelRender } from "./sns-links-panel-render";
import type { SNSLinksHandler } from "./sns-links-panel-types.d.ts";
import type { SNSLinksPanelForm } from "./sns-links-panel-form";

type InternalSnsLinksHandler = {
    updatePreviews?: () => void;
    handleSnsAdd?: () => void;
};

export class SNSLinksPanelHandler {
    private readonly form: SNSLinksPanelForm;

    /** Store the form adapter used by the panel handler. */
    public constructor(form: SNSLinksPanelForm) {
        this.form = form;
    }

    /** Resolve the external SNS handler registered for the current post. */
    public getExternalHandler(): SNSLinksHandler | null {
        const postId = this.form.getPostId();
        const map = Reflect.get(globalThis, "__snsLinksHandlers");
        if (!map || typeof map !== "object") {
            return null;
        }

        const handler = Reflect.get(map, postId);
        if (!handler) {
            return null;
        }

        const handlerMethods = this.extractHandlerMethods(handler);
        if (!handlerMethods) {
            return null;
        }

        const output: SNSLinksHandler = {};

        if (typeof handlerMethods.updatePreviews === "function") {
            output.updatePreviews = () => {
                handlerMethods.updatePreviews?.();
            };
        }

        if (typeof handlerMethods.handleSnsAdd === "function") {
            output.handleSnsAdd = () => {
                handlerMethods.handleSnsAdd?.();
            };
        }

        if (!output.updatePreviews && !output.handleSnsAdd) {
            return null;
        }

        return output;
    }

    /** Extract callable handler methods from a loose external handler object. */
    public extractHandlerMethods(handler: unknown): InternalSnsLinksHandler | null {
        if (!handler || typeof handler !== "object") {
            return null;
        }

        const updatePreviews = Reflect.get(handler, "updatePreviews");
        const handleSnsAdd = Reflect.get(handler, "handleSnsAdd");

        return {
            updatePreviews: typeof updatePreviews === "function" ? () => updatePreviews.call(handler) : undefined,
            handleSnsAdd: typeof handleSnsAdd === "function" ? () => handleSnsAdd.call(handler) : undefined,
        };
    }

    public openEditor(alsoAdd?: boolean): void {
        SNSLinksPanelRender.openEditor(this.form);

        if (alsoAdd) {
            const external = this.getExternalHandler();
            external?.handleSnsAdd?.();
        }
    }

    public applyAndClose(): void {
        this.normalizeInputs();
        this.pruneEmptyItems();

        const external = this.getExternalHandler();
        external?.updatePreviews?.();

        SNSLinksPanelRender.closeEditor(this.form);
    }

    /**
     * handle click on "+ Add SNS Link" tile inside sns-live-preview
     */
    public handleAddTileClick(event: Event): void {
        const addTile = this.form.getAddTileFromEvent(event);
        if (!addTile) {
            return;
        }

        event.preventDefault();
        this.openEditor(true);
    }

    /** Open the editor from the explicit open button. */
    public handleOpenClick(event: Event): void {
        event.preventDefault();
        this.openEditor(true);
    }

    /** Open the editor in edit mode without appending a new row. */
    public handleEditClick(event: Event): void {
        event.preventDefault();
        this.openEditor(false);
    }

    /** Apply edits and close the editor. */
    public handleCloseClick(event: Event): void {
        event.preventDefault();
        this.applyAndClose();
    }

    /**
     * Remove (×) button on SNS preview tiles
     */
    public handleTileRemoveClick(event: Event): void {
        const removeButton = this.form.getRemoveButtonFromEvent(event);
        if (!removeButton) {
            return;
        }

        const url = removeButton.getAttribute("data-url") || "";
        if (!url) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.removeUrl(url);
        this.pruneEmptyItems();

        const external = this.getExternalHandler();
        external?.updatePreviews?.();
    }

    /** Normalize values before the surrounding form submits. */
    public handleFormSubmit(_event: Event): void {
        this.normalizeInputs();
        this.pruneEmptyItems();
    }

    public normalizeInputs(): void {
        const inputs = this.form.getEditableListInputs();
        if (inputs.length === 0) {
            return;
        }

        const seen = new Set<string>();

        inputs.forEach((element) => {
            const raw = String(element.value || "").trim();
            if (!raw) {
                element.value = "";
                return;
            }

            const normalized = SNSLinksUtils.normalizeSnsLinks([raw])[0] || null;
            if (!normalized) {
                element.value = raw;
                return;
            }

            if (seen.has(normalized)) {
                element.value = "";
                return;
            }

            seen.add(normalized);
            element.value = normalized;
        });
    }

    public pruneEmptyItems(): void {
        const items = this.form.getEditableListItems();
        items.forEach((item) => {
            const input = this.form.getItemInput(item);
            if (!input) {
                return;
            }

            if (!String(input.value || "").trim()) {
                item.remove();
            }
        });
    }

    /** Remove a URL from all editable inputs. */
    public removeUrl(url: string): void {
        const target = SNSLinksUtils.normalizeSnsLinks([String(url || "").trim()])[0] || String(url || "").trim();
        if (!target) {
            return;
        }

        const inputs = this.form.getEditableListInputs();
        inputs.forEach((element) => {
            const current =
                SNSLinksUtils.normalizeSnsLinks([String(element.value || "").trim()])[0] ||
                String(element.value || "").trim();

            if (current && current === target) {
                element.value = "";
            }
        });
    }
}
